import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useAppData } from "@/hooks/useDomains";
import { sourceTotals, sumClicks, toTrend, topInRange, windowStats } from "@/lib/store";

type Range = "7" | "30";

export default function Analytics() {
  const { domains, stats } = useAppData();
  const [range, setRange] = useState<Range>("7");
  const days = range === "30" ? 30 : 7;

  const windowed = useMemo(() => windowStats(stats, days), [stats, days]);
  const extended = useMemo(() => toTrend(windowed), [windowed]);
  const ranked = useMemo(() => topInRange(domains, stats, days), [domains, stats, days]);
  const sources = useMemo(() => sourceTotals(stats, days), [stats, days]);
  const totalClicks = sumClicks(windowed);
  const avg = domains.length ? Math.round(totalClicks / domains.length) : 0;
  const top = ranked[0];
  const sourceMax = Math.max(...sources.map((item) => item.count), 1);

  const tiles = [
    { label: "区间总点击", value: totalClicks.toLocaleString(), icon: "ri-cursor-line", tone: "primary" },
    { label: "平均每域名", value: avg.toLocaleString(), icon: "ri-divide-line", tone: "accent" },
    { label: "点击最高域名", value: top && top.clicks > 0 ? top.domain : "-", icon: "ri-trophy-line", tone: "secondary" },
    { label: "活跃域名数", value: `${domains.filter((d) => d.status === "active").length} 个`, icon: "ri-links-line", tone: "primary" },
  ];

  const TONE: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-800",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-foreground-600">统计各中转域名的点击表现，帮你判断哪些跳转值得继续投放。</p>
        <div className="inline-flex w-fit gap-1 rounded-full bg-background-100 p-1">
          {[
            { key: "7" as Range, label: "近 7 天" },
            { key: "30" as Range, label: "近 30 天" },
          ].map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                range === r.key ? "bg-primary-500 font-medium text-background-50" : "text-foreground-600"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${TONE[t.tone]}`}>
              <i className={`${t.icon} text-xl`} />
            </span>
            <p className="mt-4 truncate font-heading text-xl font-semibold text-foreground-950">{t.value}</p>
            <p className="mt-1 text-sm text-foreground-500">{t.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground-950">点击走势</h3>
          <p className="mt-1 text-xs text-foreground-500">{range === "7" ? "最近 7 天" : "最近 30 天"}的总点击与独立访客</p>
        </div>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={extended} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 12, fill: "oklch(var(--foreground-500))" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid oklch(var(--background-300))",
                  fontSize: 12,
                  background: "oklch(var(--background-50))",
                  color: "oklch(var(--foreground-950))",
                }}
              />
              <Line type="monotone" dataKey="clicks" name="总点击" stroke="oklch(var(--primary-500))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="unique" name="独立访客" stroke="oklch(var(--accent-500))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="mb-4">
            <h3 className="font-heading text-base font-semibold text-foreground-950">域名点击对比</h3>
            <p className="mt-1 text-xs text-foreground-500">Top 5 域名在所选区间内的点击</p>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ranked} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="domain" width={130} tick={{ fontSize: 11, fill: "oklch(var(--foreground-700))" }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "oklch(var(--background-100))" }}
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid oklch(var(--background-300))",
                    fontSize: 12,
                    background: "oklch(var(--background-50))",
                    color: "oklch(var(--foreground-950))",
                  }}
                />
                <Bar dataKey="clicks" name="点击量" radius={[0, 6, 6, 0]}>
                  {ranked.map((item, index) => (
                    <Cell key={item.domain} fill={index % 2 === 0 ? "oklch(var(--primary-500))" : "oklch(var(--accent-500))"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="mb-4">
            <h3 className="font-heading text-base font-semibold text-foreground-950">跳转方式分布</h3>
            <p className="mt-1 text-xs text-foreground-500">按跳转类型统计域名数量</p>
          </div>
          <div className="flex flex-col gap-4">
            {(["301", "302", "mask"] as const).map((type) => {
              const count = domains.filter((d) => d.redirectType === type).length;
              const pct = domains.length ? Math.round((count / domains.length) * 100) : 0;
              const label = type === "301" ? "301 永久" : type === "302" ? "302 临时" : "隐藏跳转";
              const color = type === "301" ? "bg-primary-500" : type === "302" ? "bg-accent-500" : "bg-secondary-500";
              return (
                <div key={type}>
                  <div className="mb-1.5 flex items-center justify-between text-sm">
                    <span className="text-foreground-700">{label}</span>
                    <span className="font-medium text-foreground-950">
                      {count} 个 · {pct}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
                    <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 rounded-lg bg-background-100 p-4">
            <p className="text-xs leading-relaxed text-foreground-600">
              提示：<span className="text-foreground-900">301</span> 会更新搜索引擎索引，适合长期固定跳转；
              <span className="text-foreground-900">302</span> 适合活动引流；<span className="text-foreground-900">隐藏跳转</span> 会让地址栏保持中转域名。
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground-950">访问来源</h3>
          <p className="mt-1 text-xs text-foreground-500">按访客从哪里点进来统计。测试跳转记为直接访问。</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {sources.map((item) => {
            const pct = totalClicks ? Math.round((item.count / totalClicks) * 100) : 0;
            return (
              <div key={item.label}>
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="text-foreground-700">{item.label}</span>
                  <span className="font-medium text-foreground-950">
                    {item.count.toLocaleString()} · {pct}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.round((item.count / sourceMax) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}